import React, { Fragment, useState, useEffect, FC } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { useClient } from "ClientContext";

import { format } from "date-fns-tz";

import { useTranslation } from "react-i18next";

export interface StayDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  onChange?: (value: {
    dates: { startDate: Date | null; endDate: Date | null };
  }) => void;
}

const StayDatesRangeInput: FC<StayDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  onChange,
}) => {
  const { t } = useTranslation();
  const { global_searh, setGlobal_search } = useClient();
  const { checkIn, checkOut, guestsCount, location } = global_searh.params;
  const [startDate, setStartDate] = useState<Date | null>(checkIn);
  const [endDate, setEndDate] = useState<Date | null>(checkOut);

  // Use a ref to track previous dates to avoid infinite loop
  const previousDates = React.useRef({ startDate, endDate });

  // Call onChange only if dates have actually changed
  useEffect(() => {
    if (
      onChange &&
      (previousDates.current.startDate !== startDate ||
        previousDates.current.endDate !== endDate)
    ) {
      previousDates.current = { startDate, endDate };
      onChange({ dates: { startDate, endDate } });
    }
  }, [startDate, endDate, onChange]);

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setGlobal_search((prevState) => ({
      ...prevState,
      isSearching: false,
      params: {
        ...prevState.params,
        checkIn: start, // Update with the new start date
        checkOut: end, // Update with the new end date
        guestsCount,
        location,
      },
    }));
  };

  const renderInput = () => (
    <>
      <div className="text-neutral-300 dark:text-neutral-400">
        <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
      </div>
      <div className="flex-grow text-left">
        <span className="block xl:text-lg font-semibold">
          {startDate
            ? format(startDate, "MMM dd", { timeZone: "UTC" }) || "Add dates"
            : " "}
          {endDate
            ? ` - ${format(endDate, "MMM dd", { timeZone: "UTC" })}`
            : ""}
        </span>
        <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
          {`${t("Check in")} - ${t("Check out")}`}
        </span>
      </div>
    </>
  );

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open }) => (
        <>
          <Popover.Button
            className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none ${
              open ? "nc-hero-field-focused" : ""
            }`}
          >
            {renderInput()}
            {startDate && open && (
              <ClearDataButton onClick={() => onChangeDate([null, null])} />
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-1 z-10 mt-3 top-full w-screen max-w-sm transform px-4 sm:px-0 lg:max-w-3xl">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">


                <DatePicker
                  selected={startDate}
                  onChange={onChangeDate}
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  renderCustomHeader={(p) => (
                    <DatePickerCustomHeaderTwoMonth {...p} />
                  )}
                  renderDayContents={(day, date) => (
                    <DatePickerCustomDay dayOfMonth={day} date={date} />
                  )}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default StayDatesRangeInput;
